import React from 'react';

import debounce from 'lodash/debounce';

import { DEBOUNCE_DELAY } from '@lumapps/constants';
import { CONTENT_TYPES } from '@lumapps/content-types/keys';
import { ANCHOR_FROM_FO } from '@lumapps/contents/constants';
import { useDataAttributes } from '@lumapps/data-attributes';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { mdiTablet, mdiViewQuilt } from '@lumapps/lumx/icons';
import { useSelector } from '@lumapps/redux/react';
import { Link as RouterLink } from '@lumapps/router';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { InfiniteScroll } from '@lumapps/utils/hooks/useInfiniteScroll';

import { useContentTemplatesQuery } from '../../hooks/useContentTemplatesQuery';
import { contentCreation, contentCreationCustomTemplate } from '../../routes';
import { FIELDS, MAX_RESULTS } from './constants';

export interface TemplateMenuProps {
    /** Id of the content type owning the templates. Retrieved from the store. */
    contentTypeId: string;
    /** whether the defaul template option is enabled for this type of template */
    defaultTemplateEnabled: boolean;
    /** whether content creation link should open in new tab */
    shouldOpenInNewTab?: boolean;
    /** whether we should open the writer editor instead of the classic one */
    shouldOpenWriterEditor?: boolean;
    /** scope where this component is used. This prop will be used for tracking purposes */
    scope: string;
}

/**
 * Menu of all available content template to create content from.
 */
export const TemplateMenu: React.FC<TemplateMenuProps> = (props) => {
    const { contentTypeId, defaultTemplateEnabled, shouldOpenWriterEditor, scope, shouldOpenInNewTab } = props;
    const { get } = useDataAttributes(scope);
    const { translateKey, translateObject } = useTranslate();

    const lang = useSelector(currentLanguageSelector);
    const instance = useSelector(instanceIdSelector);

    const params = React.useMemo(
        () => ({
            fields: FIELDS,
            customContentType: contentTypeId,
            instance,
            lang,
            maxResults: MAX_RESULTS,
            showTemplatesWithInheritedOnlyParam: true,
        }),
        [contentTypeId, instance, lang],
    );

    const {
        items: templateList,
        hasNextPage,
        isInitialLoading,
        isFetchingNextPage,
        fetchNextPage,
        isLoading,
    } = useContentTemplatesQuery(params, { enabled: true, cacheTime: Infinity, staleTime: Infinity });

    const debouncedFetchNextPage = debounce(fetchNextPage, DEBOUNCE_DELAY);

    if (isInitialLoading) {
        return <Menu.ItemsSkeleton hasIcon itemCount={3} loadingMessage={translateKey(GLOBAL.LOADING)} />;
    }

    return (
        <>
            {Object.values(templateList).map((template) => (
                <Menu.Item
                    key={template.id}
                    icon={mdiViewQuilt}
                    as={RouterLink}
                    target={shouldOpenInNewTab ? '_blank' : undefined}
                    to={contentCreationCustomTemplate(contentTypeId, template.id, {
                        shouldOpenWriterEditor,
                    })}
                    {...get({ element: contentTypeId, action: 'template', type: template.id })}
                >
                    {translateObject(template.name)}
                </Menu.Item>
            ))}

            {isFetchingNextPage && (
                <Menu.ItemsSkeleton hasIcon itemCount={3} loadingMessage={translateKey(GLOBAL.LOADING)} />
            )}

            {defaultTemplateEnabled && templateList?.length > 0 && <Menu.Divider />}

            {defaultTemplateEnabled && (
                <Menu.Item
                    icon={mdiTablet}
                    as={RouterLink}
                    target={shouldOpenInNewTab ? '_blank' : undefined}
                    to={contentCreation(contentTypeId, { anchor: ANCHOR_FROM_FO })}
                    {...get({ element: contentTypeId, action: 'template', type: 'empty' })}
                >
                    {translateKey(CONTENT_TYPES.CREATE_FROM_SCRATCH)}
                </Menu.Item>
            )}

            {hasNextPage && !isLoading && <InfiniteScroll callback={() => debouncedFetchNextPage()} />}
        </>
    );
};
